/* eslint-disable camelcase */

import { ITransactionFilters } from 'services/api/ProfileApi/types/types'

import { AvailableCryptoCurrenciesList } from 'shared/models/Crypto'
import { IPaymentPayload } from 'shared/models/Payments'

import { BaseHttpService } from '../BaseHttpService'
import QueryTypes from '../QueryTypes'
import { PaymentConverter } from './PaymentConverter'
import { PaymentTypesType } from './types/common'
import {
  IDefaultFilters,
  IGetCryptoEstimateAmountPayload,
  IRequestCotePayout,
  IUploadDocumentPayload,
} from './types/types'

class ProfileApi extends BaseHttpService {
  private converter

  private cryptoServiceUrl

  private newPaymentsUrl

  private usersUrl

  private coteDivoire

  constructor(baseUrl: string) {
    super(baseUrl)
    this.baseUrl = `${baseUrl}/api/backoffice/payments`
    this.newPaymentsUrl = `${baseUrl}/api/backoffice/payments/v2`
    this.cryptoServiceUrl = `${baseUrl}/services/crypto-pay`
    this.usersUrl = `${baseUrl}/api/users`
    this.coteDivoire = `${baseUrl}/api/cote-divoire`
    this.converter = new PaymentConverter()
  }

  async getInvoiceSystemList(filters: IDefaultFilters) {
    return this.sendQuery({
      url: `${this.baseUrl}/deposit/system_list/`,
      type: QueryTypes.GET,
      data: filters,
      converterSuccess: this.converter.convertSystemList,
    })
  }

  async getPayoutSystemList(filters: IDefaultFilters) {
    return this.sendQuery({
      url: `${this.baseUrl}/payout/system_list/`,
      type: QueryTypes.GET,
      data: filters,
      converterSuccess: this.converter.convertSystemList,
    })
  }

  async getCryptoCurrencies() {
    return this.sendQuery({
      url: `${this.cryptoServiceUrl}/available-currencies`,
      type: QueryTypes.GET,
      converterSuccess: this.converter.convertCryptoCurrencies,
    })
  }

  async getCryptoMinAmount({ id, fiat_currency }: { id: AvailableCryptoCurrenciesList; fiat_currency?: string }) {
    return this.sendQuery({
      url: `${this.cryptoServiceUrl}/min-amount?currency=${id}`,
      type: QueryTypes.GET,
      data: { fiat_currency },
      converterSuccess: this.converter.convertCryptoMinAmount,
    })
  }

  async getCryptoEstimateAmount(data: IGetCryptoEstimateAmountPayload) {
    const { currencyFrom, currencyTo, amount } = data
    return this.sendQuery({
      url: `${this.cryptoServiceUrl}/estimate`,
      type: QueryTypes.GET,
      data: { currency_from: currencyFrom, currency_to: currencyTo, amount },
      converterSuccess: this.converter.convertCryptoEstimateAmount,
    })
  }

  async setDeposit(data: IPaymentPayload) {
    const { extra, amount, system, method } = data
    return this.sendQuery({
      url: `${this.newPaymentsUrl}/deposit/${system}/${method}/`,
      type: QueryTypes.POST,
      data: { extra, amount },
      converterSuccess: this.converter.convertDeposit,
    })
  }

  async setPayout(data: IPaymentPayload) {
    const { extra, amount, system, method } = data
    return this.sendQuery({
      url: `${this.newPaymentsUrl}/payout/${system}/${method}/`,
      type: QueryTypes.POST,
      data: { extra, amount },
    })
  }

  async cancelPayment(id: string, paymentType: PaymentTypesType) {
    return this.sendQuery({
      url: `${this.baseUrl}/${paymentType}/${id}/cancel_payment/`,
      type: QueryTypes.POST,
      data: { id },
    })
  }

  async uploadDocument(data: IUploadDocumentPayload) {
    return this.sendQuery({
      url: `${this.usersUrl}/documents/upload/`,
      type: QueryTypes.POST,
      data,
      options: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    })
  }

  async getCashiersList() {
    return this.sendQuery({
      url: `${this.coteDivoire}/cashiers/`,
      type: QueryTypes.GET,
      converterSuccess: this.converter.convertCashiersList,
    })
  }

  async requestCotePayout(data: IRequestCotePayout) {
    return this.sendQuery({
      url: `${this.newPaymentsUrl}/payout/cote_divoire/cash/`,
      type: QueryTypes.POST,
      data,
    })
  }

  async loadTransfersList(filters: ITransactionFilters) {
    return this.sendQuery({
      url: `${this.newPaymentsUrl}/history/player`,
      type: QueryTypes.GET,
      data: filters,
      converterSuccess: this.converter.convertNewTransfersList,
    })
  }

  async uploadPaymentScreenshot(formData: FormData, id: number) {
    return this.sendQuery({
      url: `${this.newPaymentsUrl}/deposit/${id}/documents/upload/`,
      type: QueryTypes.POST,
      data: formData,
      converterSuccess: this.converter.convertUploadScreenshots,
    })
  }
}

export default ProfileApi
